import { DocumentArrowUpIcon, TrashIcon } from "@heroicons/react/20/solid";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import Cookies from "js-cookie";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { useAlert } from "../../constants/AlertProvider";
import { getUserDetails } from "../../utils/getUserDetails";

export default function ResumeUploader({ onAutoPrefill }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [resumeUrl, setResumeUrl] = useState(null);
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [parseNote, setParseNote] = useState(null);
  const [error, setError] = useState(null);
  const showAlert = useAlert();
  const showEditProfileButton = location.pathname === "/profile";

  /* -------------------- Poll parsed resume result -------------------- */
  const pollAutoPrefill = async (taskId) => {
    // The parse runs asynchronously; poll the auto-prefill endpoint until the
    // result is ready (or we give up). Only used when a consumer wants the
    // extracted data to pre-fill a form.
    const maxAttempts = 12;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const res = await axios.get(`/common/resume/auto-prefill/${taskId}/`, {
          withCredentials: true,
        });
        const data = res.data?.auto_prefill_data;
        if (data) {
          return data;
        }
      } catch (err) {
        // 404 = not ready yet; keep polling. Anything else, stop.
        if (err?.response && err.response.status !== 404) {
          throw err;
        }
      }
      await new Promise((r) => setTimeout(r, 1500));
    }
    return null;
  };


  /* -------------------- Fetch Resume -------------------- */
  useEffect(() => {
    const fetchResume = async () => {
      try {
        setIsLoading(true);
        const userDetails = await getUserDetails();
        const userId = userDetails?.id;

        if (!userId) {
          showAlert("You are logged out", "error");
          return;
        }

        const response = await axios.get(`/api/users/${userId}/`, {
          withCredentials: true,
        });

        setResumeUrl(response.data.resume || null);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
      setIsLoading(false);
    };

    fetchResume();
  }, []);

  /* -------------------- Helpers -------------------- */
  const getFileName = (url) => {
    if (typeof url !== "string") return "";
    return url.split("/").pop();
  };

  /* -------------------- File Select -------------------- */
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];

    if (
      selectedFile &&
      (selectedFile.type === "application/pdf" ||
        selectedFile.name.toLowerCase().endsWith(".pdf"))
    ) {
      setFile(selectedFile);
      setError(null);
    } else {
      setError("Please upload a valid PDF file");
      setFile(null);
    }
  };

  /* -------------------- Upload / Update -------------------- */
  const handleUpload = async () => {
    if (!file) return;

    setIsLoading(true);
    setParseNote(null);
    try {
      const formData = new FormData();
      formData.append("file", file); // MUST match backend field name
      // Request the enhanced parser so we can pre-fill the form from the resume.
      const wantsPrefill = typeof onAutoPrefill === "function";
      if (wantsPrefill) formData.append("enhanced", "true");

      const response = await axios.post("/common/resume/upload/", formData, {
        headers: {
          "X-CSRFToken": Cookies.get("csrftoken"),
        },
        withCredentials: true,
      });

      setResumeUrl(response.data.resume);
      setFile(null);

      // If a consumer wants the parsed data, poll for it and hand it over.
      if (wantsPrefill && response.data.task_id) {
        setIsParsing(true);
        try {
          const parsed = await pollAutoPrefill(response.data.task_id);
          if (parsed) {
            // The consumer reports what it actually applied, so the message
            // reflects imported data rather than merely parsed data.
            const result = await onAutoPrefill(parsed);
            setParseNote(result?.note || "Autofilled from your resume.");
          }
        } catch (parseErr) {
          console.error("Resume parsing failed", parseErr);
        } finally {
          setIsParsing(false);
        }
      }
    } catch (err) {
      console.error(err);
      setError("Resume upload failed");
    } finally {
      setIsLoading(false);
    }
  };

  /* -------------------- Delete -------------------- */
  const handleDelete = async () => {
    setIsLoading(true);
    try {
      await axios.delete("/common/resume/upload/", {
        headers: {
          "X-CSRFToken": Cookies.get("csrftoken"),
        },
        withCredentials: true,
      });

      setResumeUrl(null);
    } catch (err) {
      console.error(err);
      setError("Failed to delete resume");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <CircularProgress color="primary" size={30} />
      </div>
    );
  }

  /* -------------------- UI -------------------- */
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Resume
        </h3>
        {showEditProfileButton && (
          <button
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
            onClick={() => navigate("/editprofile")}
          >
            <DocumentArrowUpIcon className="mr-1 h-4 w-4" />
            Edit Profile
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-2 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 rounded-md">
          {error}
        </div>
      )}

      {isParsing && (
        <div className="mb-4 flex items-center gap-2 p-2 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 rounded-md text-sm">
          <CircularProgress color="inherit" size={16} />
          Reading your resume to autofill the form…
        </div>
      )}

      {parseNote && !isParsing && (
        <div className="mb-4 p-2 text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 rounded-md text-sm">
          {parseNote} Review and edit the fields below before continuing.
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-6">
          <CircularProgress color="primary" size={30} />
        </div>
      ) : resumeUrl ? (
        /* ---------- Resume Exists ---------- */
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-md">
            <div className="flex items-center space-x-3">
              <DocumentArrowUpIcon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
              <a
                className="text-blue-600 dark:text-blue-400 hover:underline"
                href={resumeUrl}
                rel="noopener noreferrer"
                target="_blank"
              >
                {getFileName(resumeUrl)}
              </a>
            </div>
            <button
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
              onClick={handleDelete}
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>

          <div>
            <input
              accept="application/pdf"
              className="block w-full text-sm text-gray-500 dark:text-gray-400
                                file:mr-4 file:py-2 file:px-4
                                file:rounded-md file:border-0
                                file:text-sm file:font-semibold
                                file:bg-blue-50 dark:file:bg-blue-900/40 file:text-blue-700 dark:file:text-blue-300
                                hover:file:bg-blue-100 dark:hover:file:bg-blue-900/60"
              type="file"
              onChange={handleFileChange}
            />
            {file && (
              <button
                className="mt-3 w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                onClick={handleUpload}
              >
                Replace Resume
              </button>
            )}
          </div>
        </div>
      ) : (
        /* ---------- No Resume ---------- */
        <div className="space-y-4">
          <div className="text-center py-6 bg-gray-100 dark:bg-gray-900 rounded-md">
            <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              No resume uploaded yet
            </p>
          </div>

          <input
            accept="application/pdf"
            className="block w-full text-sm text-gray-500 dark:text-gray-400
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-md file:border-0
                            file:text-sm file:font-semibold
                            file:bg-blue-50 dark:file:bg-blue-900/40 file:text-blue-700 dark:file:text-blue-300
                            hover:file:bg-blue-100 dark:hover:file:bg-blue-900/60"
            type="file"
            onChange={handleFileChange}
          />

          {file && (
            <button
              className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              onClick={handleUpload}
            >
              Upload Resume
            </button>
          )}
        </div>
      )}
    </div>
  );
}
